import { Story } from '../../types/story';

export const napoliBlood: Story = {
  id: 'napoli-blood',
  title: 'Blood in the Bay',
  excerpt: 'After a brutal ambush in the Quartieri Spagnoli, two Camorra soldiers find themselves alone in a safe house, adrenaline still pumping, realizing that survival tastes like gunpowder and something far more dangerous.',
  city: 'Naples',
  publishDate: '2026-01-09',
  readTime: '24 min read',
  tags: ['♂♂', 'action', 'danger', 'intense', 'adrenaline'],
  categories: ['intense', 'queer'],
  intensity: 3,
  content: `The blood on my hands wasn't mine.

I checked again, running my fingers over my ribs, my arms, my face. No holes. No pain beyond the ringing in my ears from the gunfire. The blood belonged to Luca—splattered across my shirt when he'd taken down the last of the Russo crew with a knife to the throat.

"Marco, move!" Luca's voice cut through the chaos in my head. He was already at the door of the safe house, gun still drawn, eyes scanning the narrow alley behind us. "They'll send more. We need to get inside. Now."

I stumbled forward, my legs finally remembering how to work. The adrenaline was still coursing through me, making everything feel sharp and unreal at the same time. We'd been ambushed—six of the Russo family's soldiers waiting for us in the Quartieri Spagnoli, right in our own territory. It should have been a routine collection. Instead, it turned into a bloodbath.

Four of them were dead. Two had run. And somehow, impossibly, Luca and I had survived.

The safe house was a shithole—a third-floor apartment in a crumbling building near the port, the kind of place where nobody asked questions and nobody remembered faces. Luca kicked the door shut behind us, threw the three deadbolts, and finally lowered his gun.

"Fuck," he breathed, leaning against the door. "Fuck."

I stood in the middle of the room, still shaking, still trying to process what had just happened. We'd killed four men. Four. I'd shot two of them myself—center mass, just like Luca had taught me. Clean shots. Professional.

But my hands wouldn't stop shaking.

"Marco." Luca's voice was closer now. He'd moved without me noticing, was standing right in front of me. "Look at me."

I looked up. Luca was a mess—blood on his face, his shirt torn, a cut above his eyebrow that was still bleeding. But his eyes were steady, focused, the way they always were when things went to shit.

"You did good," he said. "You stayed calm. You didn't freeze. That's what kept us alive."

"I killed two people."

"You killed two people who were trying to kill us. There's a difference." He grabbed my shoulders, his grip firm. "This is the life, Marco. You knew that when you took the oath. You knew what it meant."

I did know. I'd grown up in Scampia, watched my father work for the Camorra until a rival clan put three bullets in his chest. I'd joined up at seventeen, desperate for the protection, the money, the respect. And Luca had taken me under his wing—taught me how to shoot, how to fight, how to survive in a world where trust was currency and betrayal was death.

But knowing and doing were different things.

"Come on," Luca said, pulling me toward the bathroom. "We need to clean up. Check for wounds."

The bathroom was as shitty as the rest of the apartment—cracked tiles, a mirror that had seen better days, a shower that probably hadn't been cleaned in months. Luca turned on the tap, let the water run until it was clear, then started washing the blood off his hands.

I watched him, still feeling disconnected from my body. He moved with practiced efficiency—checking his arms, his torso, his face for injuries. The cut above his eyebrow was the worst of it, and even that was superficial.

"Your turn," he said, stepping aside.

I moved to the sink, started washing. The water ran red, then pink, then clear. My hands were still shaking.

"Marco." Luca's hand was on my shoulder again. "You need to breathe. You're going into shock."

"I'm fine."

"You're not fine. You're shaking like a leaf and you look like you're about to pass out." He turned me around, forced me to look at him. "Breathe. In through your nose, out through your mouth. Come on."

I tried. Failed. Tried again. The air felt too thick, too heavy.

"Fuck," Luca muttered. Then he did something I didn't expect—he pulled me against his chest, one hand on the back of my head, the other wrapped around my back. "It's okay. You're okay. We're alive. That's all that matters."

I stood there, frozen, feeling his heartbeat against my cheek. It was fast, but steady. Real. Proof that we'd both made it out.

And then something shifted.

Maybe it was the adrenaline. Maybe it was the proximity, the way his body felt solid and warm against mine. Maybe it was the fact that we'd just survived something that should have killed us, and now everything felt raw and immediate and desperately alive.

I pulled back just enough to look at him, and I saw it in his eyes—the same thing I was feeling. That wild, reckless energy that came from staring down death and walking away.

"Luca—"

He kissed me.

It wasn't gentle. It was hard, desperate, tasting like blood and adrenaline and something I'd been trying not to think about for months. His hands were in my hair, on my face, pulling me closer, and I kissed back just as hard, my fingers digging into his shoulders.

We'd never talked about this. Never acknowledged the tension that had been building between us—the way his hand would linger on my shoulder during training, the way I'd catch him watching me when he thought I wasn't looking, the way we'd both find excuses to stay late at the club, just the two of us.

But now, with death still fresh in our minds and our blood still singing with survival, there was no more pretending.

He pushed me against the bathroom wall, his body pressed against mine, and I could feel him hard against my hip. I groaned into his mouth, my hands finding the hem of his blood-stained shirt, pulling it up and over his head.

He was all lean muscle and scars—a roadmap of every fight he'd survived, every close call. I traced one with my finger, a long line across his ribs, and he shuddered.

"Marco," he breathed. "If we do this—"

"I want this. I've wanted this."

"It changes everything."

"Everything already changed. Tonight. In that alley." I kissed him again, harder. "I don't care about the rules. I don't care what the family says. I just want you."

Something in his eyes went dark, hungry. He grabbed my shirt, tore it open—buttons scattering across the floor—and ran his hands over my chest, my stomach, mapping me the way I'd mapped him.

"You're sure?" he asked, his voice rough.

"Yes."

He kissed me again, then moved lower—my jaw, my neck, my collarbone. His teeth scraped against my skin, and I gasped, my head falling back against the wall. His hands were on my belt, undoing it, and then my pants were around my ankles and his hand was wrapped around me.

"Fuck, Luca—"

"Quiet," he murmured against my throat. "The walls are thin."

I bit my lip hard enough to taste blood as he stroked me, his grip firm, his rhythm perfect. He knew exactly what he was doing, and when he dropped to his knees, I thought I might lose my mind.

He took me in his mouth, and I had to clap a hand over my mouth to keep from crying out. He was good at this—too good, using his tongue and his hand in tandem, taking me deep, pulling back to tease the head. My hands found his hair, fisting in it, and he groaned around me, the vibration making my knees weak.

"Luca, I'm going to—"

He pulled off, looked up at me with those dark eyes. "Not yet. I want you inside me first."

The words sent a jolt straight through me. I pulled him up, kissed him hard, tasting myself on his tongue. "Bedroom. Now."

We stumbled to the bedroom, shedding the rest of our clothes on the way. The bed was a disaster—unmade, probably hadn't been washed in weeks—but neither of us cared. Luca pushed me down onto it, climbed on top of me, and kissed me like he was trying to consume me.

"Do you have—" I started.

"Nightstand. Left drawer. This is a safe house. They keep supplies."

I found condoms and lube, thank God, and when I turned back, Luca was on his hands and knees, looking at me over his shoulder.

"Come on, Marco. Show me what you've got."

My hands were shaking again, but for a different reason now. I slicked my fingers with lube, pressed one against him, and he pushed back with a groan.

"More," he demanded. "I can take it."

I added a second finger, then a third, working him open while he cursed and moaned into the pillow. When I finally rolled on the condom and positioned myself behind him, we were both breathing hard.

"Ready?" I asked.

"Fuck me already."

I pushed in slowly, feeling him stretch around me, tight and hot and perfect. He made a sound that was half pain, half pleasure, and I froze.

"Don't stop," he gasped. "Keep going."

I pushed in further, inch by inch, until I was fully seated inside him. We both groaned, and for a moment I just stayed there, letting him adjust, trying not to lose control.

"Move," he said. "Marco, please, move."

I started slow, pulling out almost all the way before pushing back in. But Luca wasn't having it—he pushed back hard, meeting my thrusts, demanding more.

"Harder," he growled. "I'm not going to break."

I gave him what he wanted, my hips snapping forward, the sound of skin slapping skin filling the room. He was cursing in Neapolitan now, a string of filthy words that made me even harder. I reached around, wrapped my hand around him, and he nearly came apart.

"Fuck, yes, just like that—"

I changed the angle slightly, and he cried out, his whole body tensing.

"There," he gasped. "Right there, don't stop—"

I hit that spot again and again, my hand working him in time with my thrusts. The tension was building in my spine, that familiar tightness, and I knew I wasn't going to last much longer.

"Luca, I'm close—"

"Me too. Don't stop, don't fucking stop—"

He came first, spilling over my hand with a broken moan, his body clenching around me. The sensation pushed me over the edge, and I buried myself deep with a groan, my release hitting me so hard I saw stars.

We collapsed onto the bed, both of us breathing like we'd run a marathon. I pulled out carefully, disposed of the condom, then lay down next to him. He turned to face me, and in the dim light from the window, I could see the cut above his eyebrow had started bleeding again.

"You're bleeding," I said.

"I know." He didn't move to wipe it away. "Marco—"

"If you're about to say that was a mistake—"

"I was going to say that was a long time coming." He reached out, traced my jaw with his finger. "I've wanted you since the day you walked into the club. But you were young, new, and I was supposed to be training you. Not... this."

"I'm not that young anymore. And I'm not new."

"No. You're not." He leaned in, kissed me soft this time. "But this complicates things. The family, the rules—"

"Fuck the rules. We almost died tonight. Those rules didn't save us. We saved each other." I pulled him closer. "I don't care what anyone says. I want this. I want you."

He was quiet for a long moment, his fingers tracing patterns on my chest. "The Russo family is going to retaliate. Tonight was just the beginning. It's going to get worse before it gets better."

"Then we face it together."

"You don't know what you're signing up for."

"I know exactly what I'm signing up for. I've been in this life long enough." I tilted his chin up, made him look at me. "I'm not afraid of them. I'm only afraid of losing you."

Something in his expression softened. "You're either very brave or very stupid."

"Can't I be both?"

He laughed, and it was the first real laugh I'd heard from him all night. "Yeah. You can be both."

We lay there in the darkness, listening to the sounds of Naples outside—distant sirens, shouting in the street, the ever-present hum of the city. Tomorrow we'd have to report to the boss, explain what happened, prepare for whatever came next.

But tonight, in this shitty safe house with blood still under our fingernails and the taste of each other on our lips, we were just two men who'd survived the impossible.

"Marco?" Luca's voice was quiet.

"Yeah?"

"Thank you. For having my back tonight. For not running."

"Where would I run to? You're all I've got."

He pulled me closer, his lips against my forehead. "You're all I've got too."

We fell asleep like that, tangled together, two soldiers in a war that never ended. And when I woke up a few hours later to the sound of Luca's phone buzzing with messages from the boss, I knew everything had changed.

The war with the Russo family was just beginning. But at least now I had something worth fighting for.

Something worth surviving for.

Someone.`
};
